// components/looseStone/LooseDiamondForm.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { FaCheck, FaPlus, FaUndo } from "react-icons/fa";
import Select from "react-select";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";

/* RTK queries (masters) */
import { useGetAllDiamondsQuery } from "../../toolkitServices/diamondApi";
import { useGetAllGroupedDiamondAttributesQuery } from "../../toolkitServices/diamondAttributeApi";
import { useGetDiamondSievesQuery } from "../../toolkitServices/diamondSieveApi";
import { useGetDiamondSizesQuery } from "../../toolkitServices/diamondSizeApi";
import { useGetAllBranchesQuery } from "../../toolkitServices/branchApi";
import { useGetAllVendorsQuery } from "../../toolkitServices/vendorApi";
import { useGetCountersQuery } from "../../toolkitServices/counterApi";

/* submit */
import { useAddLooseDiamondEntryMutation } from "../../toolkitServices/looseDiamondApi";
import LooseDiamondItemForm from "./LooseDiamondItemForm";

/* fields config */
export const looseDiamondFields = [
  { name: "diamondName",        label: "Diamond Name",  type: "dropdown", mandatory: true,  visibility: true },
  { name: "diamondShape",       label: "Shape",         type: "dropdown", mandatory: false, visibility: true },
  { name: "diamondClarity",     label: "Clarity",       type: "dropdown", mandatory: false, visibility: true },
  { name: "diamondColor",       label: "Color",         type: "dropdown", mandatory: false, visibility: true },
  { name: "diamondSieve",       label: "Sieve",         type: "dropdown", mandatory: false, visibility: true },
  { name: "diamondSize",        label: "Size",          type: "dropdown", mandatory: false, visibility: true },
  { name: "diamondWt",          label: "Weight (ct)",   type: "text",     mandatory: true,  visibility: true },
  { name: "diamondPcs",         label: "Pieces",        type: "text",     mandatory: false, visibility: true },
  { name: "diamondDescription", label: "Description",   type: "text",     mandatory: false, visibility: true },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (b) => ({ ...b, height: "30px" }),
  dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
  clearIndicator: (b) => ({ ...b, padding: "4px" }),
  valueContainer: (b) => ({ ...b, padding: "0 6px", height: "30px" }),
  input: (b) => ({ ...b, margin: 0, padding: 0 }),
  menu: (b) => ({ ...b, fontSize: "12px", zIndex: 50 }),
  menuList: (b) => ({ ...b, maxHeight: "140px", overflowY: "auto" }),
  singleValue: (b) => ({ ...b, fontSize: "12px" }),
};

const CARD_HEIGHT_CLASS = "h-[40vh] sm:h-[40vh]";

/* utils */
const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt3 = (v) => (Math.round(num(v) * 1000) / 1000).toFixed(3);
const fmt0 = (v) => String(Math.round(num(v)));

const rowLooksFilled = (r = {}) =>
  !!(r?.diamondName || r?.diamondWt || r?.diamondPcs || r?.diamondDescription);

const defaultRow = {
  diamondName: null,
  diamondShape: null,
  diamondClarity: null,
  diamondColor: null,
  diamondSieve: null,
  diamondSize: null,
  diamondWt: "",
  diamondPcs: "",
  diamondDescription: "",
};

export const defaultLooseDiamondValues = {
  vendor: "",
  branch: "",
  counter: "",
  looseDiamonds: [defaultRow],
};

/* inflate/deflate if you later add edit-flow */
export function inflateDiamondEntryToForm(entry) {
  if (!entry) return defaultLooseDiamondValues;
  return {
    vendor:  entry.vendorId  ? { value: entry.vendorId,  label: entry.vendorName  || "" } : "",
    branch:  entry.branchId  ? { value: entry.branchId,  label: entry.branchName  || "" } : "",
    counter: entry.counterId ? { value: entry.counterId, label: entry.counterName || "" } : "",
    looseDiamonds: [
      {
        diamondName: entry.diamondId
          ? { value: entry.diamondId, label: entry.diamondName || "" }
          : null,
        diamondShape: entry.shape
          ? { value: entry.shape, label: entry.shapeName || "" }
          : null,
        diamondClarity: entry.clarity
          ? { value: entry.clarity, label: entry.clarityName || "" }
          : null,
        diamondColor: entry.color
          ? { value: entry.color, label: entry.colorName || "" }
          : null,
        diamondSieve: entry.sieve
          ? { value: entry.sieve, label: entry.sieveName || "" }
          : null,
        diamondSize: entry.size
          ? { value: entry.size, label: entry.sizeName || "" }
          : null,
        diamondWt: entry.ct ?? "",
        diamondPcs: entry.pcs ?? "",
        diamondDescription: entry.description || "",
      },
    ],
  };
}

function deflateDiamondRow(form) {
  const r = (form?.looseDiamonds && form.looseDiamonds[0]) || {};
  return {
    diamondId:   r.diamondName?.value || null,
    diamondName: r.diamondName || null,

    shape:   r.diamondShape || null,
    clarity: r.diamondClarity || null,
    color:   r.diamondColor || null,
    sieve:   r.diamondSieve || null,
    size:    r.diamondSize || null,

    unit: "carat",
    ct: r.diamondWt ?? 0,
    pcs: r.diamondPcs ?? 0,
    description: r.diamondDescription ?? "",

    vendorId:   form.vendor?.value  || null,
    vendorName: form.vendor?.label  || "",
    branchId:   form.branch?.value  || null,
    branchName: form.branch?.label  || "",
    counterId:  form.counter?.value || null,
    counterName:form.counter?.label || "",
  };
}

const LooseDiamondForm = ({ existingData = null, onSubmitOverride, submitLabel }) => {
  const isEdit = typeof onSubmitOverride === "function";

  /* Masters */
  const { data: diamondsRes } = useGetAllDiamondsQuery();
  const { data: attrGrouped } = useGetAllGroupedDiamondAttributesQuery(); // { shape[], color[], clarity[] }
  const { data: sieveRes } = useGetDiamondSievesQuery({ page: 1, limit: 500 });
  const { data: sizeRes }  = useGetDiamondSizesQuery({ page: 1, limit: 500 });
  const { data: branches } = useGetAllBranchesQuery();
  const { data: counters } = useGetCountersQuery();
  const { data: vendors }  = useGetAllVendorsQuery({ page: 1, limit: 100, search: "" });

  /* options */
  const diamondOptions = useMemo(
    () =>
      (diamondsRes?.data || []).map((d) => ({
        value: d._id,
        label: d.diamondName || d.name || "Unnamed",
        info: d,
      })),
    [diamondsRes]
  );
  const shapeOptions = useMemo(
    () => (attrGrouped?.data?.shape || []).map((a) => ({ value: a._id, label: a.name })),
    [attrGrouped]
  );
  const clarityOptions = useMemo(
    () => (attrGrouped?.data?.clarity || []).map((a) => ({ value: a._id, label: a.name })),
    [attrGrouped]
  );
  const colorOptions = useMemo(
    () => (attrGrouped?.data?.color || []).map((a) => ({ value: a._id, label: a.name })),
    [attrGrouped]
  );
  const sieveOptions = useMemo(
    () => (sieveRes?.data || []).map((s) => ({ value: s._id, label: s.sieve, shape: s.shape })),
    [sieveRes]
  );
  const sizeOptions = useMemo(
    () => (sizeRes?.data || []).map((s) => ({ value: s._id, label: s.size, shape: s.shape })),
    [sizeRes]
  );
  const vendorOptions = useMemo(
    () => (vendors?.data || []).map((v) => ({ value: v._id, label: v.vendorName || v.name })),
    [vendors]
  );
  const branchOptions = useMemo(
    () => (branches?.data || []).map((b) => ({ value: b._id, label: b.branchName || b.name })),
    [branches]
  );

  


  /* form */
  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset,
    getValues,
    formState: { errors },
    setFocus,      // ✅ added
    trigger,       // ✅ added
    watch
  } = useForm({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: existingData
      ? { ...defaultLooseDiamondValues, ...existingData }
      : defaultLooseDiamondValues,
  });

  const { fields, append, remove } = useFieldArray({ control, name: "looseDiamonds" });
  const listRef = useRef(null);
  const firstFieldRef = useRef(null);
  const rowFirstRefs = useRef({});
  const selectedBranch = watch("branch");

const counterOptions = useMemo(() => {
  const all = counters?.data || [];
  const brId = selectedBranch?.value;
  if (!brId) return [];
  return all
    .filter((c) => String(c.branchId) === String(brId))
    .map((c) => ({ value: c._id, label: c.counterName || c.name }));
}, [counters, selectedBranch]);

// branch change => clear counter if mismatched
useEffect(() => {
  const brId = selectedBranch?.value;
  const currentCounter = getValues("counter");

  if (!brId && currentCounter) {
    setValue("counter", null, { shouldValidate: true, shouldDirty: true });
    return;
  }

  if (brId && currentCounter && currentCounter.value) {
    const match = (counters?.data || []).some(
      (c) =>
        String(c._id) === String(currentCounter.value) &&
        String(c.branchId) === String(brId)
    );
    if (!match) {
      setValue("counter", null, { shouldValidate: true, shouldDirty: true });
    }
  }
}, [selectedBranch, counters, getValues, setValue]);

  const focusRowAt = (idx) => {
    const cards = listRef.current?.querySelectorAll(".stock-card");
    if (!cards?.length) return;
    const i = Math.max(0, Math.min(idx, cards.length - 1));
    const card = cards[i];
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
    const id = card?.dataset?.fieldId;
    const el = rowFirstRefs.current[id];
    if (el?.focus) el.focus();
  };

  /* live totals */
  const rowsWatch = useWatch({ control, name: "looseDiamonds" });
  const totals = useMemo(() => {
    const rows = Array.isArray(rowsWatch) ? rowsWatch : [];
    let ct = 0, pcs = 0, itemsValid = 0;
    for (const r of rows) {
      ct  += num(r?.diamondWt);
      pcs += num(r?.diamondPcs);
      if (rowLooksFilled(r || {})) itemsValid++;
    }
    return { itemsAll: rows.length, itemsValid, ct, pcs };
  }, [rowsWatch]);

  /* ---------- NEW: Guard last row before adding next ---------- */
  const REQUIRED_ROW_FIELDS = ["diamondName", "diamondWt"];

  const validateLastRowOrFocus = async () => {
    const rows = getValues("looseDiamonds") || [];
    const lastIdx = Math.max(0, rows.length - 1);
    const last = rows[lastIdx] || {};

    const missing = REQUIRED_ROW_FIELDS.filter((f) => {
      const v = last?.[f];
      if (f === "diamondName") return !v || !v.value; // react-select object
      return v === "" || v === null || v === undefined;
    });

    if (!missing.length) return true;

    // trigger field-level validation to show errors
    await trigger(missing.map((f) => `looseDiamonds.${lastIdx}.${f}`));
    // focus first missing field
    setFocus(`looseDiamonds.${lastIdx}.${missing[0]}`, { shouldSelect: true });
    // toast.error("Please complete required fields in current row.");
    return false;
  };

  const guardedAppendRow = async () => {
    const ok = await validateLastRowOrFocus();
    if (!ok) return;
    append({ ...defaultRow });
    requestAnimationFrame(() =>
      setTimeout(() => focusRowAt((getValues("looseDiamonds")?.length || 1) - 1), 0)
    );
  };
  /* ---------- /NEW ---------- */

  /* submit */
  const [addEntry, { isLoading: isSavingCreate }] = useAddLooseDiamondEntryMutation();

  const onSubmit = async (form) => {
    const rows = Array.isArray(form.looseDiamonds) ? form.looseDiamonds : [];
    const looseDiamonds = rows.filter(rowLooksFilled);
    if (!looseDiamonds.length) {
      toast.error("Add at least one diamond row.");
      return;
    }

    if (isEdit) {
      try {
        await onSubmitOverride(deflateDiamondRow(form));
      } catch { /* parent handles */ }
      return;
    }

    try {
      const payload = [];
      for (const r of looseDiamonds) {
        const body = {
          diamondId:   r?.diamondName?.value || null,
          diamondName: r?.diamondName || null,
          shape:   r?.diamondShape || null,
          clarity: r?.diamondClarity || null,
          color:   r?.diamondColor || null,
          sieve:   r?.diamondSieve || null,
          size:    r?.diamondSize || null,
          unit: "carat",
          ct:  r?.diamondWt ?? 0,
          pcs: r?.diamondPcs ?? 0,
          description: r?.diamondDescription ?? "",
          vendorId:   form?.vendor?.value  || null,
          vendorName: form?.vendor?.label  || "",
          branchId:   form?.branch?.value  || null,
          branchName: form?.branch?.label  || "",
          counterId:  form?.counter?.value || null,
          counterName:form?.counter?.label || "",
        };
        payload.push(body);
      }
      await addEntry(payload).unwrap();
      toast.success("Loose diamonds saved.");
      reset(defaultLooseDiamondValues);
      requestAnimationFrame(() => {
        focusRowAt(0);
        setTimeout(() => firstFieldRef.current?.focus(), 0);
      });
    } catch (err) {
      const msg = err?.data?.message || err?.data?.msg || err?.error || "Failed to add loose diamonds";
      toast.error(msg);
    }
  };

  /* keyboard shortcuts (updated to use guard) */
  useEffect(() => {
    const onKeys = (e) => {
      if (e.ctrlKey && e.key === "+") {
        if (isEdit) return;
        e.preventDefault();
        guardedAppendRow(); // ✅ guarded
      }
      if (e.ctrlKey && e.key.toLowerCase() === "r") {
        e.preventDefault();
        reset(existingData ? { ...defaultLooseDiamondValues, ...existingData } : defaultLooseDiamondValues);
        requestAnimationFrame(() => {
          focusRowAt(0);
          setTimeout(() => firstFieldRef.current?.focus(), 0);
        });
      }
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [handleSubmit, reset, existingData, isEdit]); // append removed since we use guardedAppendRow

  /* top field options */
  const topFields = [
    { name: "vendor",  label: "Vendor",  type: "dropdown", mandatory: false },
    { name: "branch",  label: "Branch",  type: "dropdown", mandatory: false },
    { name: "counter", label: "Counter", type: "dropdown", mandatory: false },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <motion.div
        initial={{ opacity: 0.9, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="rounded-xl bg-white overflow-hidden"
      >
        {/* Top: Vendor / Branch / Counter */}
        <div className="bg-white shadow-sm rounded-lg px-3 py-2 border mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {topFields.map((f) => (
              <div key={f.name} className="min-w-0">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {f.label} {f.mandatory && <span className="text-red-500">*</span>}
                </label>
                <Controller
                  name={f.name}
                  control={control}
                  rules={{ required: f.mandatory ? `${f.label} is required` : false }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={
                        f.name === "vendor"
                          ? vendorOptions
                          : f.name === "branch"
                          ? branchOptions
                          : counterOptions
                      }
                      styles={selectStyles}
                      classNamePrefix="react-select"
                      placeholder={`Select ${f.label}`}
                      isClearable
                      menuPlacement="auto"
                       isDisabled={f.name === "counter" && !selectedBranch}
                    />
                  )}
                />
                {errors[f.name] && (
                  <p className="text-red-500 text-[11px] mt-1">{errors[f.name].message}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="bg-white shadow-sm rounded-xl p-0">
          <div ref={listRef} className={`relative ${CARD_HEIGHT_CLASS} overflow-y-auto`}>
            {fields.map((f, rowindex) => (
              <LooseDiamondItemForm
                key={f.id}
                fieldId={f.id}
                control={control}
                register={register}
                errors={errors}
                rowindex={rowindex}
                setValue={setValue}
                firstFieldRef={rowindex === 0 ? firstFieldRef : undefined}
                registerFirstField={(id, el) => {
                  if (el) rowFirstRefs.current[id] = el;
                }}
                cardHeightClass={CARD_HEIGHT_CLASS}
                /* options */
                diamondOptions={diamondOptions}
                shapeOptions={shapeOptions}
                clarityOptions={clarityOptions}
                colorOptions={colorOptions}
                sieveOptions={sieveOptions}
                sizeOptions={sizeOptions}
                /* remove row */
                onDelete={() => remove(rowindex)}
              />
            ))}
          </div>

          {/* Summary */}
          <div className="border rounded-lg bg-slate-50/70 px-3 py-2 mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12px] font-semibold text-slate-600">Summary</span>
              <span className="text-[11px] text-slate-500">
                Items: <b>{totals.itemsValid}</b> / {totals.itemsAll}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              <SummaryChip label="Total Pcs" value={fmt0(totals.pcs)} />
              <SummaryChip label="Total Ct" value={fmt3(totals.ct)} />
            </div>
          </div>

          {/* Actions */}
          <div className="p-2 flex flex-wrap gap-2 justify-between">
            {!isEdit && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white py-2 px-4 rounded-md text-sm font-medium shadow-sm"
                onClick={guardedAppendRow} // ✅ guarded
              >
                <FaPlus className="text-sm" />
                Add Row (Ctrl + +)
              </motion.button>
            )}

            <div className="flex gap-2 ml-auto">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="submit"
                disabled={isEdit ? false : isSavingCreate}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white py-2 px-4 rounded-md text-sm font-semibold shadow-sm"
              >
                <FaCheck className="text-sm" />
                {submitLabel || (isSavingCreate ? "Saving..." : "Save (Ctrl + S)")}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => {
                  reset(
                    existingData
                      ? { ...defaultLooseDiamondValues, ...existingData }
                      : defaultLooseDiamondValues
                  );
                  requestAnimationFrame(() => {
                    focusRowAt(0);
                    setTimeout(() => firstFieldRef.current?.focus(), 0);
                  });
                }}
                className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white py-2 px-4 rounded-md text-sm font-semibold shadow-sm"
              >
                <FaUndo className="text-sm" />
                Reset (Ctrl + R)
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </form>
  );
};

const SummaryChip = ({ label, value }) => (
  <div className="rounded-lg border bg-white px-3 py-2 shadow-sm">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-800">{value}</div>
  </div>
);

export default LooseDiamondForm;
